import { fetchAuthSession } from 'aws-amplify/auth';
import { lambdaEndpoints } from '../aws-config';

export interface MCPKey {
  api_key: string;
  created_at: string;
}

class MCPKeyService {
  private async getAuthHeaders(): Promise<HeadersInit> {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }

  /**
   * Get user's current MCP API key
   */
  async getCurrentKey(): Promise<MCPKey | null> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(lambdaEndpoints.mcpKeyManager, {
        method: 'GET',
        headers,
      });

      if (response.status === 404) {
        return null; // No key exists yet
      }

      if (!response.ok) {
        throw new Error('Failed to fetch API key');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error fetching MCP key:', error);
      throw error;
    }
  }

  /**
   * Generate a new MCP API key
   */
  async generateKey(): Promise<MCPKey> {
    try {
      const session = await fetchAuthSession();
      const headers = await this.getAuthHeaders();

      // Extract refresh token from session for auto-refresh capability
      // In Amplify v6, refresh token is stored in credentials object
      const refreshToken = (session as any).tokens?.signInDetails?.loginId
        ? localStorage.getItem(`CognitoIdentityServiceProvider.6m9pn1kmh9t9fn40ce04ur2o18.LastAuthUser`)
        : null;

      // Get refresh token from localStorage (Amplify stores it there)
      const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.6m9pn1kmh9t9fn40ce04ur2o18.LastAuthUser');
      const cognitoRefreshToken = lastAuthUser
        ? localStorage.getItem(`CognitoIdentityServiceProvider.6m9pn1kmh9t9fn40ce04ur2o18.${lastAuthUser}.refreshToken`)
        : null;

      const response = await fetch(lambdaEndpoints.mcpKeyManager, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          cognito_refresh_token: cognitoRefreshToken
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate API key');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error generating MCP key:', error);
      throw error;
    }
  }

  /**
   * Refresh JWT token for existing API key
   * Call this periodically when user is active to keep the token fresh
   */
  async refreshToken(): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(lambdaEndpoints.mcpKeyManager, {
        method: 'PATCH',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to refresh API key token');
      }
    } catch (error) {
      // console.error('Error refreshing MCP key token:', error);
      // Silently fail - not critical
    }
  }

  /**
   * Revoke current MCP API key
   */
  async revokeKey(): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(lambdaEndpoints.mcpKeyManager, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to revoke API key');
      }
    } catch (error) {
      // console.error('Error revoking MCP key:', error);
      throw error;
    }
  }
}

export const mcpKeyService = new MCPKeyService();
